import * as React from 'react';
import { Box, Link, Stack, Typography } from '@mui/material';
import { styled } from '@mui/system';

const Styled = {
  Container: styled(Box)({
    backgroundColor: 'transparent',
    padding: '0 2em 2em 6em',
    ['@media (max-width:1100px)']: {
      padding: '0 32px 48px'
    }
  }),
  LinkStack: styled(Box)({
    display: 'inline-flex',
    gap: '32px',
    marginTop: '36px',
    ['@media (max-width:500px)']: {
      display: 'flex',
      flexDirection: 'column'
    }
  }),
  LinkSection: styled(Box)(({ theme }) => ({
    display: 'inline-block',
    width: '45ch',
    '& i': {
      color: theme.palette.GOLD_1.main,
      fontSize: '1.5rem', 
      lineHeight: '2rem', 
    }
  })),
  LinkTitle: styled(Typography)(({ theme }) => ({
    fontSize: '1.4rem',
    color: theme.palette.GOLD_1.main,
    paddingBottom: '0.3em'
  }))
};

interface ResourceList {
  icon: string;
  text: string;
  url: string;
}

interface LinkListProps {
  list: ResourceList[];
  title: string;
}

const educationLinks: ResourceList[] = [
  {
    icon: 'ri-book-2-fill',
    text: 'How to Become a Chef',
    url: 'https://www.escoffier.edu/blog/culinary-pastry-careers/how-to-become-a-chef'
  },
  {
    icon: 'ri-book-2-fill',
    text: 'The 7 Segments of the Culinary Industry',
    url: 'https://www.escoffier.edu/blog/culinary-pastry-careers/how-to-become-a-chef/#segments'
  },
  {
    icon: 'ri-book-2-fill',
    text: 'Complete Guide to Culinary School Scholarships',
    url: 'https://www.escoffier.edu/blog/financing-your-education/complete-guide-to-culinary-school-scholarships/'
  },
  {
    icon: 'ri-headphone-fill',
    text: 'The Ultimate Dish Podcast',
    url: 'https://www.escoffier.edu/podcast/'
  }
];

const careerLinks: ResourceList[] = [
  {
    icon: 'ri-book-2-fill',
    text: 'Five Tips for Landing the Job Interview',
    url: 'https://www.escoffier.edu/blog/culinary-pastry-careers/five-tips-for-nailing-the-job-interview/'
  },
  {
    icon: 'ri-book-2-fill',
    text: 'Interviewing Tips for the Cooking Industry',
    url: 'https://www.escoffieronline.com/interviewing-tips-for-the-cooking-industry/'
  },
  {
    icon: 'ri-book-2-fill',
    text: 'How Chefs Can Build Standout Resumes',
    url: 'https://www.escoffier.edu/blog/culinary-pastry-careers/how-chefs-can-build-standout-resumes/'
  }
];

const LinkList = ({ list, title }: LinkListProps): JSX.Element => {
  const linkSx = {
    fontSize: '1rem',
    fontWeight: 500,
    color: 'black', 
  };

  return (
    <Styled.LinkSection>
      <Styled.LinkTitle variant="EC_TYPE_SM">{title}</Styled.LinkTitle>
      {list.map((link) => (
        <Stack direction="row" spacing={1} marginTop="12px" key={link.text}>
          <i className={link.icon} />
          {link.url.includes('www.escoffier.edu') ? (
            <Link
              href={link.url}
              target="_blank"
              rel="noopener"
              underline="hover"
              variant="EC_TYPE_BASE"
              sx={linkSx}
            >
              {link.text}
            </Link>
          ) : (
            <Link
              href={link.url}
              target="_blank"
              rel="noopener"
              underline="hover"
              variant="EC_TYPE_BASE"
              sx={linkSx}
            >
              {link.text}
            </Link>
          )}
        </Stack>
      ))}
    </Styled.LinkSection>
  );
};

const ResourcesSection: React.FC = () => {
  return (
    <Styled.Container>
      <Styled.LinkStack>
        <LinkList list={educationLinks} title="Educational Resources" />
        <LinkList list={careerLinks} title="Career Resources" />
      </Styled.LinkStack>
    </Styled.Container>
  );
};

export default ResourcesSection;
